import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ApplePay, UIElement, SubmitData, AddressData, SubmitActions } from '@adyen/adyen-web';

import {
  APPLE_PAY_CONFIG,
  ApplePayPaymentDetailsRef,
  createAdyenApplePayFinalizeRequestBody,
} from '../utils';
import {
  ESdkEvents,
  EPaymentProvider,
  EBEPaymentMethodsName,
  AdyenFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../../../../../contracts';
import { sendFEEvent } from '../../../../../../../eventUtil';
import { useAPI } from '../../../../../../../services/api.service';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';
import { useAdyenCheckout } from '../../../../AdyenPaymentElement/hooks/use-adyen-checkout';
import { generateAppchargeErrorMessage } from '../../../../../../../utils/generate-appcharge-error-message';
import { apmLatencyTracker } from '../../../../../../../utils/apm-latency-tracker';

export function UseAdyenApplePay(onReady?: (isAvailable: boolean) => void) {
  const isAdyenPaymentInProgress = useRef(false);
  const { startOrderPolling } = useOrderPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const { checkoutData, getEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const applePaymentDetailsRef = useRef<ApplePayPaymentDetailsRef>();
  const [applePay, setApplePay] = useState<UIElement | null>(null);
  const { API } = useAPI();
  const applePayPM = useMemo(
    () =>
      checkoutData?.paymentMethods.find(
        (pm) =>
          pm.provider === EPaymentProvider.adyen &&
          pm.paymentMethodName === EBEPaymentMethodsName.apple,
      ),
    [checkoutData?.paymentMethods],
  );
  const { checkoutInstance } = useAdyenCheckout(applePayPM);

  const currencyCode = checkoutData?.pricing?.currencyCode || '';

  const onAuthorized = (
    data: {
      authorizedEvent: ApplePayJS.ApplePayPaymentAuthorizedEvent;
      billingAddress?: Partial<AddressData>;
      deliveryAddress?: Partial<AddressData>;
    },
    actions: {
      resolve: () => void;
      reject: (error?: ApplePayJS.ApplePayError) => void;
    },
  ) => {
    const applePayPayment = data.authorizedEvent.payment;
    applePaymentDetailsRef.current = {
      email: applePayPayment.shippingContact?.emailAddress || '',
      paymentMethodDetails: applePayPayment.token.paymentMethod,
    };
    actions.resolve();
  };

  const onSubmit = async (state: SubmitData, _: UIElement, actions: SubmitActions) => {
    if (isAdyenPaymentInProgress.current) {
      return;
    }
    isAdyenPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    try {
      const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
        createAdyenApplePayFinalizeRequestBody(
          state.data,
          applePaymentDetailsRef.current!,
          applePayPM!,
        ),
      );
      handleServerResponse(finalizePaymentResponse, actions);
    } catch {
      handleError();
    }
  };

  const onPaymentFailed = () => {
    handleError();
  };

  const onPaymentCompleted = () => {
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
      ...getEventParams(),
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
    startOrderPolling(checkoutData?.orderId!);
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    isAdyenPaymentInProgress.current = false;
  };

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Apple Pay', applePayPM?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    let applePay: ApplePay;
    if (checkoutInstance) {
      applePay = new ApplePay(checkoutInstance, {
        ...APPLE_PAY_CONFIG,
        amount: {
          value: priceBreakdown?.priceTotal || 0,
          currency: currencyCode,
        },
        configuration: applePayPM?.paymentMethodConfig?.configuration,
        onAuthorized,
        onSubmit,
        onPaymentFailed,
        onPaymentCompleted,
        onError: (error) => {
          console.log('onError Apple Pay', { error });
          handleError();
        },
      });

      applePay
        ?.isAvailable()
        .then(() => onReady?.(true))
        .catch(() => {
          setApplePay(null);
          onReady?.(false);
        })
        .finally(() => {
          try {
            apmLatencyTracker.endTracking('Apple Pay', applePayPM?.id, {
              paymentMethodName: applePayPM?.paymentMethodName,
              paymentMethodDisplayName: applePayPM?.paymentMethodDisplayName,
              provider: 'Adyen',
              type: 'Express',
            });
          } catch (error) {
            console.error('Failed to end APM latency tracking:', error);
          }
        });

      setApplePay(applePay);
    }
  }, [checkoutInstance, priceBreakdown?.priceTotal, applePayPM?.id]);

  const handleServerResponse = (
    finalizePaymentResponse: AdyenFinalizePaymentResponse,
    actions: SubmitActions,
  ) => {
    const { status, resultCode, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      actions.resolve({ resultCode: resultCode, action });
    } else if (status === 'failed') {
      actions.resolve({ resultCode: resultCode });
      handleError(finalizePaymentResponse.appChargeErrorType);
    }
  };

  return {
    applePay,
  };
}
